import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, Truck, ShieldCheck, Ticket, Sparkles, Check, AlertCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import Image from '../components/Image';
import { getTranslation as t } from '../utils/translations';
import { validateCoupon } from '../api/client';
import DynamicSectionsRenderer from '../components/DynamicSectionsRenderer';

const CART_IMAGE_FALLBACK = '/product-fallback.svg';

const CartPage = ({ cart, onUpdateQty, onRemove, storeSettings }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [promoCodeInput, setPromoCodeInput] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [couponSuccess, setCouponSuccess] = useState('');
    const [allProducts, setAllProducts] = useState([]);
    const [allCategories, setAllCategories] = useState([]);

    // Check logged in user from localStorage
    const loggedInUser = useMemo(() => {
        try {
            const rawAuth = localStorage.getItem('luxvono_customer_auth') || localStorage.getItem('user');
            if (rawAuth) {
                const parsed = JSON.parse(rawAuth);
                return parsed?.user || parsed;
            }
        } catch (_) {}
        return null;
    }, []);

    // Load recommendations context
    useEffect(() => {
        const loadCartContext = async () => {
            try {
                const [productsRes, categoriesRes] = await Promise.all([
                    import('../api/client').then(m => m.getProducts(0, 50)),
                    import('../api/client').then(m => m.getCategories())
                ]);
                setAllProducts(productsRes?.data || []);
                setAllCategories(categoriesRes || []);
            } catch (e) {
                console.warn('Failed to load cart recommendations:', e);
            }
        };
        loadCartContext();
    }, []);

    const displayCart = Array.isArray(cart) ? cart : [];
    const subtotal = displayCart.reduce((sum, item) => sum + (item.selectedVariant?.salePrice || item.selectedVariant?.price || item.price || 0) * (item.qty || 1), 0);
    const shipping = displayCart.length === 0 ? 0 : subtotal >= 750 ? 0 : 24;
    const estimatedTax = 0;

    // Available coupons pool (strictly from backend store settings)
    const activeCouponsPool = useMemo(() => {
        return Array.isArray(storeSettings?.coupons) ? storeSettings.coupons : [];
    }, [storeSettings]);

    // Validate and calculate discount
    const calculateDiscount = (coupon, currentSubtotal, currentShipping) => {
        if (!coupon) return 0;
        if (coupon.discountType === 'PERCENTAGE') {
            const pctVal = (currentSubtotal * (Number(coupon.discountValue) || 0)) / 100;
            return coupon.maxDiscountCap ? Math.min(pctVal, Number(coupon.maxDiscountCap)) : pctVal;
        }
        if (coupon.discountType === 'FIXED_AMOUNT') {
            return Math.min(currentSubtotal, Number(coupon.discountValue) || 0);
        }
        if (coupon.discountType === 'FREE_SHIPPING') {
            return currentShipping;
        }
        return 0;
    };

    // Apply Coupon Logic (Server-Side Validated)
    const applyCouponCode = async (rawCode, notifyOnSuccess = true) => {
        if (!rawCode || !rawCode.trim()) {
            setCouponError('يرجى كتابة كود الخصم');
            setCouponSuccess('');
            return;
        }
        const clean = rawCode.trim().toUpperCase();
        setCouponError('');

        try {
            // Primary: Validate against backend promotions database
            const res = await validateCoupon(clean, subtotal);
            if (res && res.valid) {
                const discountVal = Number(res.discountAmount) || calculateDiscount(res, subtotal, shipping);
                const couponObject = {
                    code: res.code,
                    title: res.description || res.code,
                    discountType: res.discountType,
                    discountValue: res.discountValue,
                    discountAmount: discountVal,
                    minOrderAmount: res.minOrderAmount
                };

                setAppliedCoupon(couponObject);
                localStorage.setItem('luxvono_active_coupon', JSON.stringify(couponObject));
                setPromoCodeInput(res.code);
                if (notifyOnSuccess) {
                    setCouponSuccess(`تم تفعيل كود الخصم (${res.code}) بنجاح! وفرت $${discountVal.toLocaleString()} ✨`);
                }
                return;
            } else if (res && res.message) {
                setCouponError(res.message);
                setCouponSuccess('');
                return;
            }
        } catch (serverErr) {
            // Secondary fallback: Verified active promotions in store settings if backend temporarily unreachable
            const found = activeCouponsPool.find(c => c.code && c.code.toUpperCase() === clean);
            if (found && found.active !== false) {
                const minOrder = Number(found.minOrderAmount) || 0;
                if (subtotal < minOrder) {
                    const diff = minOrder - subtotal;
                    setCouponError(`الحد الأدنى لتفعيل كود (${clean}) هو $${minOrder.toLocaleString()}. ينقصك $${diff.toLocaleString()} إضافية للتفعيل.`);
                    setCouponSuccess('');
                    return;
                }
                const discountVal = calculateDiscount(found, subtotal, shipping);
                const couponObject = {
                    code: found.code,
                    title: found.titleAr || found.titleEn || found.code,
                    discountType: found.discountType,
                    discountValue: found.discountValue,
                    discountAmount: discountVal,
                    minOrderAmount: found.minOrderAmount
                };
                setAppliedCoupon(couponObject);
                localStorage.setItem('luxvono_active_coupon', JSON.stringify(couponObject));
                setPromoCodeInput(found.code);
                if (notifyOnSuccess) {
                    setCouponSuccess(`تم تفعيل كود الخصم (${found.code}) بنجاح! وفرت $${discountVal.toLocaleString()} ✨`);
                }
                return;
            }
        }

        setCouponError(`كود الخصم (${clean}) غير صحيح أو منتهي الصلاحية.`);
        setCouponSuccess('');
    };

    // Remove Coupon
    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setPromoCodeInput('');
        setCouponSuccess('');
        setCouponError('');
        localStorage.removeItem('luxvono_active_coupon');
    };

    // Auto-apply from URL query or localStorage on mount
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const urlCoupon = queryParams.get('coupon') || queryParams.get('promo');
        if (urlCoupon) {
            applyCouponCode(urlCoupon, true);
        } else {
            try {
                const stored = localStorage.getItem('luxvono_active_coupon');
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (parsed && parsed.code) {
                        applyCouponCode(parsed.code, false);
                    }
                }
            } catch (_) {}
        }
    }, [location.search, subtotal]);

    // Recalculate dynamic discount amount on cart subtotal change
    const activeDiscountAmount = useMemo(() => {
        if (!appliedCoupon) return 0;
        return calculateDiscount(appliedCoupon, subtotal, shipping);
    }, [appliedCoupon, subtotal, shipping]);

    const total = Math.max(0, subtotal - activeDiscountAmount + shipping + estimatedTax);

    return (
        <div className="page-cart">
            <div className="container">
                <DynamicSectionsRenderer
                    page="cart-page"
                    position="top"
                    storeSettings={storeSettings}
                    allProducts={allProducts}
                    allCategories={allCategories}
                />

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
                    <button 
                        type="button" 
                        onClick={() => {
                            if (window.history.state && window.history.state.idx > 0) {
                                navigate(-1);
                            } else {
                                navigate('/');
                            }
                        }} 
                        className="back-link" 
                        style={{ 
                            background: 'none', 
                            border: 'none', 
                            cursor: 'pointer', 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '6px', 
                            padding: '4px 0', 
                            color: 'inherit', 
                            fontSize: '14px', 
                            fontWeight: '600' 
                        }}
                        aria-label="رجوع"
                    >
                        <ChevronLeft size={18} /> 
                        <span>{t('BACK') || 'رجوع'}</span>
                    </button>
                </div>

                <div className="section-header" style={{ marginBottom: '28px' }}>
                    <div>
                        <div className="section-kicker">Cart experience</div>
                        <h1 className="section-title">{t('YOUR_CART')}</h1>
                        <p className="section-subtitle">
                            Stable pricing, cleaner quantity controls, and a checkout summary that stays in view while shoppers review variants.
                        </p>
                        <div className="section-accent" />
                    </div>
                </div>

                {displayCart.length === 0 ? (
                    <div className="cart-empty">
                        <ShoppingBag size={52} strokeWidth={1.2} />
                        <p>{t('CART_EMPTY')}</p>
                        <Link to="/category/all" className="btn-primary">{t('CONTINUE_SHOPPING')}</Link>
                    </div>
                ) : (
                    <>
                        <div className="cart-layout">
                            <div className="cart-items">
                                {displayCart.map((item, idx) => {
                                    const unitPrice = item.selectedVariant?.salePrice || item.selectedVariant?.price || item.price || 0;
                                    const lineTotal = unitPrice * (item.qty || 1);

                                    return (
                                        <div key={`${item.id}-${idx}`} className="cart-item">
                                            <Image
                                                src={item.image}
                                                fallbackSrc={CART_IMAGE_FALLBACK}
                                                alt={item.name}
                                                className="cart-item-img"
                                                lazy={true}
                                            />
                                            <div className="cart-item-info">
                                                <span className="cart-item-category">{item.category || 'Curated luxury'}</span>
                                                <h3 className="cart-item-name">{item.name}</h3>

                                                {item.selectedVariant && (
                                                    <div className="cart-item-variant-info" style={{ fontSize: '0.9em', color: 'var(--text-muted)', marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                        {item.selectedVariant.sku ? (
                                                            <span className="trust-badge" style={{ minHeight: '34px' }}>SKU {item.selectedVariant.sku}</span>
                                                        ) : null}
                                                        {item.selectedVariant.attributes && Object.entries(item.selectedVariant.attributes).map(([key, value]) => (
                                                            <span key={key} className="trust-badge" style={{ minHeight: '34px' }}>
                                                                {key}: {value}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}

                                                <span className="cart-item-price" style={{ color: '#FFFFFF', fontWeight: 800 }}>${unitPrice.toLocaleString()}</span>
                                                <div style={{ marginTop: '10px', color: 'var(--text-muted)', fontSize: '13px' }}>
                                                    Line total: <strong style={{ color: '#FFFFFF' }}>${lineTotal.toLocaleString()}</strong>
                                                </div>
                                            </div>

                                            <div className="cart-item-actions">
                                                <div className="qty-control">
                                                    <button type="button" onClick={() => onUpdateQty(idx, -1)} aria-label="Decrease quantity">
                                                        <Minus size={14} />
                                                    </button>
                                                    <span>{item.qty}</span>
                                                    <button type="button" onClick={() => onUpdateQty(idx, 1)} aria-label="Increase quantity">
                                                        <Plus size={14} />
                                                    </button>
                                                </div>

                                                <button className="cart-remove-btn" type="button" onClick={() => onRemove(idx)} aria-label="Remove item">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <aside className="cart-summary">
                                <h3>{t('ORDER_SUMMARY')}</h3>
                                <div className="cart-summary-row">
                                    <span>{t('SUBTOTAL')}</span>
                                    <span style={{ fontWeight: 600 }}>${subtotal.toLocaleString()}</span>
                                </div>

                                {/* Active Coupon Golden Discount Row */}
                                {appliedCoupon && activeDiscountAmount > 0 && (
                                    <div className="cart-summary-row" style={{
                                        color: '#FFFFFF',
                                        background: 'rgba(255, 255, 255, 0.08)',
                                        border: '1px dashed #FFFFFF',
                                        padding: '8px 12px',
                                        borderRadius: 10,
                                        fontWeight: 700
                                    }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Sparkles size={14} />
                                            خصم الكوبون ({appliedCoupon.code})
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: '1.05rem', fontWeight: 900 }}>-${activeDiscountAmount.toLocaleString()}</span>
                                            <button
                                                type="button"
                                                onClick={handleRemoveCoupon}
                                                title="إزالة الكوبون"
                                                style={{
                                                    background: 'rgba(239, 68, 68, 0.2)',
                                                    border: 'none',
                                                    color: '#f87171',
                                                    borderRadius: '50%',
                                                    width: 20,
                                                    height: 20,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 11
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="cart-summary-row">
                                    <span>Shipping estimate</span>
                                    <span>{shipping === 0 ? t('COMPLIMENTARY') : `$${shipping.toLocaleString()}`}</span>
                                </div>
                                <div className="cart-summary-row">
                                    <span>Estimated tax</span>
                                    <span>{estimatedTax === 0 ? '$0.00' : `$${estimatedTax.toLocaleString()}`}</span>
                                </div>

                                <div className="cart-summary-row cart-total" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.25)', paddingTop: 14, marginTop: 10 }}>
                                    <span style={{ fontWeight: 800 }}>{t('TOTAL')}</span>
                                    <span style={{ color: '#FFFFFF', fontWeight: 900, fontSize: '1.35rem' }}>${total.toLocaleString()}</span>
                                </div>

                                {/* Promo Code Input Box */}
                                <div className="checkout-field" style={{ marginTop: '22px' }}>
                                    <label htmlFor="promo-code" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                        <span>كود الخصم أو القسيمة (PROMO CODE)</span>
                                        {appliedCoupon && (
                                            <span style={{ color: '#10b981', fontSize: '0.78rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <Check size={13} /> مفعل
                                            </span>
                                        )}
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            id="promo-code"
                                            className="checkout-input"
                                            placeholder="أدخل كود الخصم (مثال: WELCOME10)"
                                            value={promoCodeInput}
                                            onChange={(e) => {
                                                setPromoCodeInput(e.target.value.toUpperCase());
                                                setCouponError('');
                                                setCouponSuccess('');
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    applyCouponCode(promoCodeInput, true);
                                                }
                                            }}
                                            style={{
                                                textTransform: 'uppercase',
                                                letterSpacing: '1px',
                                                fontWeight: 700,
                                                borderColor: appliedCoupon ? '#FFFFFF' : undefined
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => applyCouponCode(promoCodeInput, true)}
                                            style={{
                                                background: 'linear-gradient(135deg, #FFFFFF 0%, #E2E8F0 100%)',
                                                color: '#000',
                                                border: 'none',
                                                borderRadius: 10,
                                                padding: '0 16px',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            <Ticket size={16} />
                                            <span>تطبيق</span>
                                        </button>
                                    </div>

                                    {/* Feedback messages */}
                                    {couponSuccess && (
                                        <div style={{ color: '#10b981', fontSize: '0.8rem', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Check size={14} />
                                            {couponSuccess}
                                        </div>
                                    )}
                                    {couponError && (
                                        <div style={{ color: '#f87171', fontSize: '0.8rem', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <AlertCircle size={14} />
                                            {couponError}
                                        </div>
                                    )}
                                </div>

                                <div className="checkout-trust-grid" style={{ marginTop: 18 }}>
                                    <div className="checkout-trust-card">
                                        <strong><Truck size={14} style={{ verticalAlign: 'middle', marginRight: '8px' }} /> Delivery</strong>
                                        Shipping is complimentary once the cart crosses the luxury free-delivery threshold.
                                    </div>
                                    <div className="checkout-trust-card">
                                        <strong><ShieldCheck size={14} style={{ verticalAlign: 'middle', marginRight: '8px' }} /> Protection</strong>
                                        Every order carries secure payment handling and tracked fulfillment messaging.
                                    </div>
                                </div>

                                <Link
                                    to="/checkout"
                                    className="btn-primary btn-lg"
                                    style={{
                                        width: '100%',
                                        marginTop: '22px',
                                        textAlign: 'center',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        background: 'linear-gradient(135deg, #FFFFFF 0%, #E2E8F0 100%)',
                                        color: '#000',
                                        fontWeight: 800
                                    }}
                                >
                                    {t('PROCEED_CHECKOUT')} • ${total.toLocaleString()}
                                </Link>
                            </aside>
                        </div>

                        <div className="cart-summary cart-summary-mobile" style={{ marginTop: '20px' }}>
                            <div className="cart-summary-row cart-total" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                                <span>{displayCart.length} items</span>
                                <span style={{ color: '#FFFFFF', fontWeight: 900, fontSize: '1.25rem' }}>${total.toLocaleString()}</span>
                            </div>
                            <Link
                                to="/checkout"
                                className="btn-primary btn-lg"
                                style={{
                                    width: '100%',
                                    marginTop: '16px',
                                    textAlign: 'center',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    background: 'linear-gradient(135deg, #FFFFFF 0%, #E2E8F0 100%)',
                                    color: '#000',
                                    fontWeight: 800
                                }}
                            >
                                {t('PROCEED_CHECKOUT')}
                            </Link>
                        </div>
                    </>
                )}

                <DynamicSectionsRenderer
                    page="cart-page"
                    position="bottom"
                    storeSettings={storeSettings}
                    allProducts={allProducts}
                    allCategories={allCategories}
                />
                <DynamicSectionsRenderer
                    page="cart-page"
                    position="before-footer"
                    storeSettings={storeSettings}
                    allProducts={allProducts}
                    allCategories={allCategories}
                />
            </div>
        </div>
    );
};

export default CartPage;
